import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Star, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { fetchTestimonials, resolveImageUrl } from '../services/api';
import { testimonials as mockTestimonials } from '../mock';
import { Fireflies, Moth } from '../components/FairyElements';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const whatsappLink = 'https://wa.me/919681424041';

  useEffect(() => {
    fetchTestimonials()
      .then((data) => {
        if (Array.isArray(data) && data.length) setTestimonials(data);
      })
      .catch(() => { /* keep mock */ })
      .finally(() => setLoading(false));
  }, []);

  // Auto-advance carousel every 6s
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(id);
  }, [testimonials.length]);

  const prev = () => setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setActiveIndex((i) => (i + 1) % testimonials.length);

  const active = testimonials[activeIndex];

  return (
    <div className="min-h-screen bg-[#0a1612]">
      {/* Hero */}
      <section
        className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative overflow-hidden"
        style={{ '--forest-image': "url(/mystical-garden/blog-library.png)", '--bg-opacity': 0.35 }}
      >
        <Fireflies count={40} />
        <Moth delay={0} top="25%" />
        <Moth delay={6} top="65%" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <span className="vine-divider block w-32 mx-auto" />
          <h1 className="text-5xl md:text-7xl font-bold mb-4 gradient-text text-glow" data-testid="testimonials-title">
            Whispers from Souls Guided
          </h1>
          <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto font-serif-mystical italic">
            Heartfelt words from those who walked the mystical path with The Divine7777.
          </p>
        </div>
      </section>

      {/* Carousel */}
      {loading ? (
        <div className="py-24 text-center text-amber-100/60">Loading testimonials...</div>
      ) : testimonials.length === 0 ? (
        <div className="py-24 text-center text-amber-100/60">No testimonials yet — be the first to share your experience.</div>
      ) : (
        <section className="section-deep py-20 px-4 md:px-8 lg:px-16 relative" data-testid="testimonials-carousel">
          <Fireflies count={25} />

          <div className="max-w-4xl mx-auto relative z-10">
            <Card className="forest-card border-0 shadow-2xl">
              <CardContent className="p-10 md:p-14 text-center">
                <div className="flex gap-1 justify-center mb-6">
                  {[...Array(active.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>

                {active.image ? (
                  <img
                    src={resolveImageUrl(active.image)}
                    alt={active.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-6 border-2 border-amber-500/50 mystical-glow-gold"
                  />
                ) : null}

                <p className="text-xl md:text-2xl text-amber-100/90 italic font-serif-mystical leading-relaxed mb-8" data-testid="active-testimonial-text">
                  "{active.text}"
                </p>

                <p className="text-amber-300 text-lg font-semibold tracking-wider" style={{ fontFamily: 'Cinzel, serif' }}>
                  — {active.name}
                </p>
                {active.location ? (
                  <p className="text-sm text-amber-100/60 mt-1">{active.location}</p>
                ) : null}
              </CardContent>
            </Card>

            {/* Controls */}
            {testimonials.length > 1 ? (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  variant="ghost"
                  onClick={prev}
                  className="rounded-full w-12 h-12 p-0 bg-emerald-900/60 border border-amber-500/40 text-amber-300 hover:bg-emerald-800"
                  data-testid="testimonial-prev-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>

                <div className="flex gap-2">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === activeIndex ? 'w-8 bg-amber-400' : 'w-2 bg-amber-100/30'
                      }`}
                      aria-label={`Go to testimonial ${i + 1}`}
                      data-testid={`testimonial-dot-${i}`}
                    />
                  ))}
                </div>

                <Button
                  variant="ghost"
                  onClick={next}
                  className="rounded-full w-12 h-12 p-0 bg-emerald-900/60 border border-amber-500/40 text-amber-300 hover:bg-emerald-800"
                  data-testid="testimonial-next-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* All Testimonials Grid */}
      {testimonials.length > 1 ? (
        <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/services-grove.png)", '--bg-opacity': 0.25 }}>
          <Fireflies count={20} />
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-3">Every Voice, Every Story</h2>
              <p className="text-amber-100/70 font-serif-mystical italic">All testimonials gathered from kindred souls</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <Card key={t.id} className="forest-card border-0" data-testid={`testimonial-card-${t.id}`}>
                  <CardHeader>
                    <div className="flex gap-1 mb-2">
                      {[...Array(t.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      {t.image ? (
                        <img src={resolveImageUrl(t.image)} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-amber-500/40" />
                      ) : null}
                      <div>
                        <CardTitle className="text-lg text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>{t.name}</CardTitle>
                        {t.location ? <CardDescription className="text-amber-100/60">{t.location}</CardDescription> : null}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-amber-100/80 italic font-serif-mystical">"{t.text}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA */}
      <section className="section-deep py-20 px-4 text-center">
        <h3 className="text-3xl md:text-4xl font-bold gradient-text mb-4">Begin Your Own Story</h3>
        <p className="text-amber-100/70 max-w-2xl mx-auto mb-8 font-serif-mystical italic">
          Step into the mystical garden — let the cards reveal what your soul has been whispering.
        </p>
        <Button
          size="lg"
          className="btn-gold px-10 py-7 text-base tracking-wider uppercase"
          onClick={() => window.open(whatsappLink, '_blank')}
          data-testid="testimonials-book-btn"
          style={{ fontFamily: 'Cinzel, serif' }}
        >
          <MessageCircle className="mr-2 h-5 w-5" />
          Book Your Reading
        </Button>
      </section>
    </div>
  );
};

export default Testimonials;
